/**
 * Wall-clock timestamp sync engine — implementation-plan.md §1.3a, executed literally.
 *
 *   at session start:  anchor pair = { video_t0: video.currentTime, wall_t0: performance.now() }
 *   every audio chunk: { chunk_start_wall, chunk_duration, chunk_index }
 *   map chunk → video:
 *     wall_time  = chunk_start_wall + (byte_offset / total_bytes) × chunk_duration
 *     video_time = video_t0 + (wall_time − wall_t0) / 1000
 *   on pause/seek:     capture a new anchor pair, accumulate total_pause_duration; all
 *                      post-pause wall times subtract total_pause_duration before mapping
 *
 * `performance.now()` is monotonic, so mapping never drifts with the system clock, and the
 * transcription API is never asked for word timestamps — which is why Callout can run on
 * gpt-4o-mini-transcribe at $0.003/min.
 *
 * The engine keeps TWO clocks: the session origin (video_t0/wall_t0, re-based on
 * resume/seek) and the audit trail of every anchor pair that goes to
 * TaggingSession.anchor_pairs.
 */

export const SYNC_TOLERANCE_MS = 500;

/** One (video, wall) observation. Serialised as `{video_time, wall_time}` on the session row. */
export interface AnchorPair {
  /** `video.currentTime` at the moment the pair was taken, in seconds. */
  videoTime: number;
  /** `performance.now()` at the same moment, in milliseconds. */
  wallTime: number;
}

/** Timing envelope for one uploaded audio chunk (plan §1.3a step 2). */
export interface ChunkTiming {
  chunkIndex: number;
  /** Wall-clock ms at which the chunk's first byte was captured. */
  chunkStartWall: number;
  /** Wall-clock length of the chunk in ms (independent of the video timeline). */
  chunkDurationMs: number;
  /** Encoded size of the chunk, used for intra-chunk interpolation. */
  totalBytes: number;
}

/** The plan's `createAnchorPair()`. */
export function createAnchorPair(videoTime: number, wallTime: number): AnchorPair {
  return { videoTime, wallTime };
}

/** Wall clock the engine anchors against. `performance.now()` per the plan. */
export function nowWallMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/**
 * Stateless projection for a finished session: the first pair is the origin and no pause
 * bookkeeping is applied (use `SyncEngine` while capturing — it owns the pause ledger).
 */
export function mapAudioToVideo(
  wallTimeMs: number,
  pairs: readonly AnchorPair[],
  toleranceMs = SYNC_TOLERANCE_MS,
): number | null {
  const origin = pairs[0];
  if (!origin) return null;
  const videoS = origin.videoTime + (wallTimeMs - origin.wallTime) / 1000;
  if (videoS < origin.videoTime) return origin.videoTime;
  // Guard against a nonsense caller passing a wall time far outside the session.
  if (Math.abs(wallTimeMs - origin.wallTime) > 12 * 60 * 60 * 1000 + toleranceMs) return null;
  return videoS;
}

export interface ChunkWindow {
  startS: number;
  endS: number;
}

/**
 * Live clock-sync state for one tagging session. Every method is synchronous and pure with
 * respect to the wall clock it is handed, so the whole engine is testable without a browser.
 */
export class SyncEngine {
  private readonly trail: AnchorPair[] = [];
  private origin: AnchorPair | null = null;
  private pausedTotalMs = 0;
  private pausedSinceOriginMs = 0;
  private pauseStartedAt: number | null = null;

  constructor(pairs: readonly AnchorPair[] = []) {
    for (const pair of pairs) this.anchor(pair.videoTime, pair.wallTime);
  }

  /** Audit trail for `TaggingSession.anchor_pairs`. */
  get anchorPairs(): readonly AnchorPair[] {
    return this.trail;
  }

  /** Cumulative pause across the session (plan §1.3a step 4). */
  get totalPauseDurationMs(): number {
    return this.pausedTotalMs;
  }

  /** True while the video is paused and a pause ledger entry is open. */
  get isPaused(): boolean {
    return this.pauseStartedAt !== null;
  }

  /** Session origin, i.e. the plan's `{video_t0, wall_t0}`. */
  get sessionOrigin(): AnchorPair | null {
    return this.origin;
  }

  /**
   * Records an anchor pair. During uninterrupted playback the session origin is kept — this
   * is only an audit-trail sample (re-basing on every sample would make the origin follow the
   * most recent observation and mis-map anything captured before it).
   */
  anchor(videoTime: number, wallTime: number): AnchorPair {
    const pair = createAnchorPair(videoTime, wallTime);
    this.trail.push(pair);
    if (!this.origin) {
      this.origin = pair;
      this.pausedSinceOriginMs = 0;
    }
    return pair;
  }

  /** Video paused: open a pause ledger entry. */
  onPause(wallTime: number): void {
    if (this.pauseStartedAt === null) this.pauseStartedAt = wallTime;
  }

  /**
   * Video resumed: close the pause, then re-base the session origin on the current position
   * so that post-pause wall times map through the new (video_t, wall_t) pair.
   */
  onResume(videoTime: number, wallTime: number): AnchorPair {
    this.closePause(wallTime, true);
    return this.rebase(videoTime, wallTime);
  }

  /** Video seeked: the pre-seek origin is void; re-base on the new position. */
  seek(videoTime: number, wallTime: number): AnchorPair {
    this.closePause(wallTime, false);
    return this.rebase(videoTime, wallTime);
  }

  /**
   * Maps a wall-clock instant onto the video timeline.
   * Returns null when the session has no origin yet (never a silent 0).
   * Wall times after a pause start hold the paused frame (plan §1.3a edge case).
   */
  mapAudioToVideo(wallTimeMs: number): number | null {
    const origin = this.origin;
    if (!origin) return null;

    let effectiveWall = wallTimeMs;
    if (this.pauseStartedAt !== null && wallTimeMs > this.pauseStartedAt) {
      effectiveWall = this.pauseStartedAt;
    }

    const videoS =
      origin.videoTime + (effectiveWall - origin.wallTime - this.pausedSinceOriginMs) / 1000;
    if (!Number.isFinite(videoS)) return null;
    return videoS < origin.videoTime ? origin.videoTime : videoS;
  }

  /** Plan §1.3a step 3: byte offset inside a chunk → wall time → video time. */
  mapByteOffsetToVideo(chunk: ChunkTiming, byteOffset: number): number | null {
    return this.mapAudioToVideo(this.wallTimeAtByteOffset(chunk, byteOffset));
  }

  /** The plan's `wall_time = chunk_start_wall + (byte_offset / total_bytes) × chunk_duration`. */
  wallTimeAtByteOffset(chunk: ChunkTiming, byteOffset: number): number {
    if (!chunk.totalBytes || chunk.totalBytes <= 0) return chunk.chunkStartWall;
    const ratio = Math.min(1, Math.max(0, byteOffset / chunk.totalBytes));
    return chunk.chunkStartWall + ratio * chunk.chunkDurationMs;
  }

  /** Video window covered by a whole chunk, clamped at a pause boundary. */
  mapChunkToVideo(chunk: ChunkTiming): ChunkWindow | null {
    const startS = this.mapAudioToVideo(chunk.chunkStartWall);
    const endS = this.mapAudioToVideo(chunk.chunkStartWall + chunk.chunkDurationMs);
    if (startS === null || endS === null) return null;
    return { startS, endS: Math.max(startS, endS) };
  }

  /** Anchor-pair snapshot in the D1 column shape (`anchor_pairs` JSON). */
  toJSON(): { video_time: number; wall_time: number }[] {
    return serializeAnchorPairs(this.trail);
  }

  private rebase(videoTime: number, wallTime: number): AnchorPair {
    const pair = createAnchorPair(videoTime, wallTime);
    this.trail.push(pair);
    this.origin = pair;
    this.pausedSinceOriginMs = 0;
    return pair;
  }

  private closePause(wallTime: number, countSinceOrigin: boolean): void {
    if (this.pauseStartedAt === null) return;
    const delta = Math.max(0, wallTime - this.pauseStartedAt);
    this.pausedTotalMs += delta;
    if (countSinceOrigin) this.pausedSinceOriginMs += delta;
    this.pauseStartedAt = null;
  }
}

/** D1 `TaggingSession.anchor_pairs` serialisation (snake_case per db/schema.sql). */
export function serializeAnchorPairs(pairs: readonly AnchorPair[]) {
  return pairs.map((p) => ({ video_time: Number(p.videoTime.toFixed(3)), wall_time: Number(p.wallTime.toFixed(3)) }));
}

export function deserializeAnchorPairs(
  rows: readonly { video_time: number; wall_time: number }[],
): AnchorPair[] {
  return rows.map((r) => ({ videoTime: r.video_time, wallTime: r.wall_time }));
}

export interface MediaElementLike {
  currentTime: number;
  paused: boolean;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

/**
 * Wires an HTMLVideoElement to a SyncEngine: anchors on load, samples during playback,
 * ledgers pauses and re-bases on resume/seek. Returns the detach function.
 */
export function attachMediaElement(
  engine: SyncEngine,
  video: MediaElementLike,
  { sampleEveryMs = 5_000, now = nowWallMs }: { sampleEveryMs?: number; now?: () => number } = {},
): () => void {
  engine.anchor(video.currentTime, now());

  const onPlaying = () => {
    if (engine.isPaused) engine.onResume(video.currentTime, now());
  };
  const onPause = () => engine.onPause(now());
  const onSeeked = () => engine.seek(video.currentTime, now());

  let timer: ReturnType<typeof setInterval> | null = null;
  const startSampling = () => {
    if (timer !== null) return;
    timer = setInterval(() => {
      if (!video.paused) engine.anchor(video.currentTime, now());
    }, sampleEveryMs);
  };
  const stopSampling = () => {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };

  video.addEventListener('loadedmetadata', () => engine.anchor(video.currentTime, now()));
  video.addEventListener('play', () => {
    onPlaying();
    startSampling();
  });
  // `playing` fires once playback ACTUALLY resumes (after a seek or a buffer stall), which is
  // when the media clock restarts: re-base there or every mapping stays late by the stall.
  video.addEventListener('playing', () => engine.seek(video.currentTime, now()));
  video.addEventListener('pause', onPause);
  video.addEventListener('ended', onPause);
  video.addEventListener('seeked', onSeeked);
  video.addEventListener('emptied', stopSampling);
  if (!video.paused) startSampling();

  return () => {
    stopSampling();
    video.removeEventListener('pause', onPause);
    video.removeEventListener('seeked', onSeeked);
  };
}
